import dayjs, { Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { AccountDetails } from '../types'

dayjs.extend(customParseFormat)
dayjs.extend(advancedFormat)
dayjs.extend(utc)
dayjs.extend(timezone)

interface GetAccountTypeNameArgs {
  type: string
  account?: AccountDetails
}

export const amountFormatter: Intl.NumberFormat = new Intl.NumberFormat(
  'en-US',
  {
    style: 'currency',
    currency: 'USD',
  },
)

export function roundTwoDecimals(num: number): string {
  return (Math.round(num * 100) / 100).toFixed(2)
}

export function getAccountTypeName({
  type,
  account,
}: GetAccountTypeNameArgs): string {
  switch (type) {
    case 'DDA':
      return 'Spending'
    case 'SDA':
      return 'Savings'
    case 'IRA_SDA':
      return 'IRA Savings'
    case 'MMA':
      return 'Money Market'
    case 'CDA':
      return account?.productDescription || 'Certificate of Deposit'
    case 'CMG':
      return 'CMG Account'
    default:
      return 'ERROR'
  }
}

// example: 2021-10-16T20:18:44-04:00 -> Oct 16, 2021
export function getServerTimeShortDate(dateString: string): string {
  if (!dateString) return ''
  /* If the first 10 chars match YYYY-MM-DD pattern use those  chars as the date to remove the time.
    This will prevent any conversions that may happen in different timezones because of the time.*/
  const date = dateString.substring(0, 10).match(/(\d{4})-(\d{2})-(\d{2})/)
    ? dateString.substring(0, 10)
    : dateString

  const formattedDate = new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })

  return formattedDate
}

// example: 2021-10-16T20:18:44-04:00 -> October 16
export function getServerTimeShortDateWithoutYear(dateString: string): string {
  const date = dateString.substring(0, 10).match(/(\d{4})-(\d{2})-(\d{2})/)
    ? dateString.substring(0, 10)
    : dateString

  const formattedDate = new Date(date).toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })

  return formattedDate
}

// example: 2021-10-16T20:18:44-04:00 -> Oct 16
export function getServerTimeShortMonthDateWithoutYear(
  dateString: string,
): string {
  const date = dateString.substring(0, 10).match(/(\d{4})-(\d{2})-(\d{2})/)
    ? dateString.substring(0, 10)
    : dateString

  const formattedDate = new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })

  return formattedDate
}

export function getServerTimeInEst(): Dayjs {
  return dayjs().tz('America/New_York')
}

export function getMonthName(date: string): string {
  return dayjs(date).format('MMMM')
}

// Nov 11, 2022
export function getDateWthAbbrName(date: string): string {
  return dayjs(date).format('MMM D, YYYY')
}

// Nov 11, 2022
export function getServerDateWithAbbrName(date: string): string {
  return dayjs(date).tz('America/New_York').format('MMM D, YYYY')
}

// Tuesday, Jan 24, 2023
export function getDateWithDayAbbrMonth(date: string): string {
  return dayjs(date).format('dddd, MMM Do, YYYY')
}

function padTo2Digits(num: number): string {
  return num.toString().padStart(2, '0')
}

// example: "10/06/2021"
export function getSlashDate(date: string): string {
  if (Number.isNaN(Date.parse(date))) return ''
  const d = new Date(date)
  return `${padTo2Digits(d.getMonth() + 1)}/${padTo2Digits(
    d.getDate(),
  )}/${d.getFullYear()}`
}

// TODO: Test getSlashDate at all the places and replace with new getSlashDateV2
// example: "10/06/2021"
export function getSlashDateV2(date: string): string {
  if (!dayjs(date).isValid()) return ''
  return dayjs(date).tz('America/New_York').format('MM/DD/YYYY')
}

export const formatPercentage = (value: number): string => {
  return `${value.toFixed(2)}%`
}

// Parses an input date including the given timezone.
// For example, "2022-06-04T14:10:20-04:00" will always
// return "2:10 PM ET"
export function getServerTime(date: string): string {
  const toFormat = dayjs(date, 'YYYY-MM-DDTHH:mm:ssZ')
  return dayjs(toFormat).tz('America/New_York').format('h:mm A [ET]')
}

// take input date as string
// For example, "2022-06-03T01:00:00-04:00"
// return difference of days in number from current date
export const getDaysDifference = (date: string): number => {
  const today = dayjs()
  const difference = today.diff(date, 'day')
  return difference
}

export function addDays(
  date: string,
  daysToAdd?: number,
  useBusinessDays?: boolean,
): Date {
  const startDate = new Date(date)
  const days = daysToAdd || 0

  if (useBusinessDays) {
    for (let i = 1; i <= days; i += 1) {
      startDate.setDate(startDate.getDate() + 1)
      if (startDate.getDay() === 6) {
        startDate.setDate(startDate.getDate() + 2)
      } else if (startDate.getDay() === 0) {
        startDate.setDate(startDate.getDate() + 1)
      }
    }
  } else {
    startDate.setDate(startDate.getDate() + days)
  }

  return startDate
}

export const maskAccountNumber = (accountNumber = ''): string => {
  return accountNumber.length > 1
    ? `••${accountNumber.slice(-4)}`
    : accountNumber
}

/**
 * formatDateForEST
 * Formats a date string 2021-11-15 into EST timezone string 2021-11-15T00:00:00-04:00
 * @param date
 * @returns string
 */
export function formatDateForEST(date: string): string {
  if (!date) return ''
  return dayjs.tz(date, 'YYYY-MM-DD', 'America/New_York').format()
}

/**
 * formatDateForApi
 * Formats a date string into YYYY-MM-DD format required by some endpoints
 * @param date
 * @returns string
 */
export const formatDateForApi = (date: string): string => {
  return dayjs(date).format('YYYY-MM-DD')
}

/**
 * formatMessage
 * @param message a message
 * @param valueObj a list of values
 * @returns formatted message
 * formatMessage('This is {date}', { date: '12/28/2021' }) => This is 12/28/2021
 */

type ValueObjType = {
  [key: string]: string
}

export const formatMessage = (
  message: string,
  valueObj: ValueObjType,
): string => message.replace(/\{(\w+)\}/g, (a, b) => valueObj[b])

export interface DateOptions {
  date?: string
  tz?: string
  format?: string
}

/**
 * getDatePickerDate
 * @param date date string to format (optional - default: server time)
 * @param tz timezone to use (optional - default: America/New York)
 * @param format date format to return (optional - default: MM/DD/YYYY)
 * @returns string date expected by Metronome datepicker
 */
export const getDatePickerDate = ({
  date,
  tz = 'America/New_York',
  format = 'MM/DD/YYYY',
}: DateOptions): string => {
  return dayjs(date).tz(tz).format(format).toString()
}

export const stringToInitialCaps = (str: string): string => {
  if (str) {
    return str.toLowerCase().replace(/^./, str[0].toUpperCase())
  }
  return ''
}

export const stringToLowerCaseWithDashes = (str: string): string => {
  return str.replace(/\s+/g, '-').toLowerCase()
}
